import { useEffect, useMemo, useState } from "react";
import { api } from "../../../api/apiClient";
import type { Location, ShopifyOrderSkipReason, WebOrdersReport, WebOrdersReportQuery } from "../../../api/types";
import { SKIP_REASON_LABEL } from "../webOrderAttention";
import { useAuth } from "../../../state/AuthContext";
import { periodDays, periodFromParams, type ReportPeriodValue } from "../../../utils/reportPeriods";
import { shopColorSlots } from "../../../utils/shopColorSlots";
import { euro, formatDayLabel } from "../../finances/opcostFormat";
import { EmptyState } from "../../ui/EmptyState";
import { Select } from "../../ui/Select";
import { StatTile } from "../../ui/StatTile";
import { ByShopTable } from "./ByShopTable";
import { OrderHistoryTable } from "./OrderHistoryTable";
import { OrderJourneySheet } from "./OrderJourneySheet";
import { CouriersTable, PeopleTable } from "./PeopleTables";
import { ReportPeriodPicker } from "./ReportPeriodPicker";
import { queryParams } from "./reportLinks";
import { ShopDistribution } from "./ShopDistribution";

/**
 * G-143 — the "By shop" tab of Shopify orders: where the period's web
 * orders went, who did the work, and what they booked. Read-only and loaded
 * on demand; unlike the "To handle" tab it does not poll. The server scopes
 * a manager to their own shop and leaves cost out (OD-218, OD-220).
 */
/** "1 made outside the CRM · 1 test order" — the skip reasons of a period. */
function skippedText(byReason: Partial<Record<ShopifyOrderSkipReason, number>>): string | undefined {
  const parts = (Object.keys(SKIP_REASON_LABEL) as ShopifyOrderSkipReason[])
    .filter((reason) => (byReason[reason] ?? 0) > 0)
    .map((reason) => `${byReason[reason]} ${SKIP_REASON_LABEL[reason].toLowerCase()}`);
  return parts.length ? parts.join(" · ") : undefined;
}

export function WebOrdersByShop({ initialParams }: { initialParams: URLSearchParams }) {
  const { hasRole, user } = useAuth();
  const seesAllShops = hasRole("OWNER", "ADMIN");
  const [period, setPeriod] = useState<ReportPeriodValue>(() => periodFromParams(initialParams));
  const [shopId, setShopId] = useState(seesAllShops ? (initialParams.get("shop") ?? "") : "");
  const [locations, setLocations] = useState<Location[]>([]);
  const [report, setReport] = useState<WebOrdersReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  const days = periodDays(period);
  const from = days?.from;
  const to = days?.to;
  const query = useMemo<WebOrdersReportQuery | null>(
    () => (from && to ? { from, to, ...(shopId ? { locationId: shopId } : {}) } : null),
    [from, to, shopId],
  );

  useEffect(() => {
    api
      .listLocations()
      .then((rows) => setLocations(rows.filter((l) => l.isActive)))
      .catch(() => setLocations([]));
  }, []);

  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    setError(null);
    api
      .getWebOrdersReport(query)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load the web orders report");
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const slots = useMemo(
    () => shopColorSlots(locations.length ? locations : (report?.shops.map((s) => s.shop) ?? [])),
    [locations, report],
  );
  const allShops = seesAllShops && !shopId;
  const idleShops = useMemo(
    () => locations.filter((l) => !report?.shops.some((s) => s.shop.id === l.id)),
    [locations, report],
  );
  const params = useMemo(() => queryParams(query ?? {}), [query]);

  const totals = report?.totals;
  const notTaken = report?.notTaken;

  return (
    <div className="wo-report">
      <div className="card an-filters">
        <ReportPeriodPicker value={period} onChange={setPeriod} />
        <div className="field">
          <label htmlFor="wo-shop">Shop</label>
          {seesAllShops ? (
            <Select id="wo-shop" value={shopId} onChange={(e) => setShopId(e.target.value)}>
              <option value="">All shops</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          ) : (
            <p id="wo-shop" className="an-note">
              {user?.assignedLocation?.name ?? "No shop assigned"}
            </p>
          )}
        </div>
        {report ? (
          <span className="muted wo-period-days">
            {formatDayLabel(report.period.from)} – {formatDayLabel(report.period.to)}
          </span>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {/* No figures while the custom range is incomplete or too long: the last
          period's numbers under the new dates would read as an answer. */}
      {query && report && totals && notTaken ? (
        <>
          <div className="stat-tile-row">
            {/* G-160 (OD-265): what came from Shopify and where it went. */}
            <StatTile
              label="From Shopify"
              value={totals.received}
              hint={allShops ? `${totals.received - notTaken.received} taken · ${notTaken.received} not taken` : undefined}
            />
            <StatTile
              label="Through the CRM"
              value={totals.handedOver}
              hint={`${totals.delivered} delivered${totals.cancelled ? ` · ${totals.cancelled} cancelled` : ""}`}
            />
            <StatTile label="Skipped" value={totals.skipped} hint={skippedText(totals.skippedByReason)} />
            <StatTile
              label="Still open"
              value={totals.stillOpen}
              hint={totals.notMade ? `${totals.notMade} can't be made` : undefined}
            />
            <StatTile
              label="Booked as sales"
              value={euro(totals.money.bookedEur)}
              hint={`bouquets ${euro(totals.money.bouquetsEur)} · items ${euro(totals.money.itemsEur)}${
                Number(totals.money.refundedEur) > 0 ? ` · after ${euro(totals.money.refundedEur)} refunded` : ""
              }`}
            />
            <StatTile label="Customer paid in Shopify" value={euro(totals.money.customerPaidEur)} />
          </div>

          {totals.received === 0 ? (
            <div className="card">
              <EmptyState title="No web orders in this period" />
            </div>
          ) : (
            <>
              <section className="card an-card" aria-labelledby="wo-by-shop">
                <div className="an-card-head">
                  <h3 id="wo-by-shop">By shop</h3>
                </div>
                {allShops ? <ShopDistribution report={report} slots={slots} /> : null}
                <ByShopTable
                  report={report}
                  slots={slots}
                  idleShops={idleShops}
                  allShops={allShops}
                  onPickShop={seesAllShops && !shopId ? setShopId : null}
                />
              </section>

              {report.people.length ? (
                <section className="card an-card" aria-labelledby="wo-people">
                  <div className="an-card-head">
                    <h3 id="wo-people">Who did the work</h3>
                  </div>
                  <PeopleTable report={report} query={params} />
                </section>
              ) : null}

              {report.couriers.length ? (
                <section className="card an-card" aria-labelledby="wo-couriers">
                  <div className="an-card-head">
                    <h3 id="wo-couriers">Couriers</h3>
                  </div>
                  <CouriersTable report={report} query={params} />
                </section>
              ) : null}

              {query ? (
                <section className="card an-card" aria-labelledby="wo-history">
                  <div className="an-card-head">
                    <h3 id="wo-history">Every web order</h3>
                  </div>
                  <OrderHistoryTable query={query} total={totals.received} onOpen={setOpenOrderId} />
                </section>
              ) : null}
            </>
          )}
        </>
      ) : null}

      {openOrderId ? <OrderJourneySheet orderId={openOrderId} onClose={() => setOpenOrderId(null)} /> : null}
    </div>
  );
}
